<template>
  <section @click="selectedMod = null">
    <Tabs @selected="updateClickEvent">
      <Tab label="Fetch mods">
        <section>
          <form @submit.prevent="explorePlayer">
            <label for="ally_code">Ally code:</label>
            <input
              type="text"
              name="ally_code"
              id="ally_code"
              placeholder="123456789"
              v-model="allyCodeToFetch"
            />
            <input type="submit" value="Fetch mods" />
          </form>
          <form @submit.prevent="parseModsOptimizerJson">
            <label for="optimizer_json">Mods Optimizer JSON</label>
            <input
              type="file"
              name="optimizer_json"
              id="optimizier_json"
              @change="processFile"
              accept=".json"
            />
            <input type="submit" value="Import Mods Optimizer export" />
          </form>
          <div v-if="userMessage">{{ userMessage }}</div>
          <div class="error-message" v-if="errorMessage">
            {{ errorMessage }}
          </div>
        </section>
      </Tab>
      <Tab label="Filter mod type">
        <section class="toolbox">
          <Filter
            @change="(tiers) => (selectedTiers = tiers)"
            title="Tier"
            :values="tiers"
          />
          <Filter
            @change="(rarity) => (selectedRarity = rarity)"
            title="Rarity"
            :values="rarity"
          />
          <Filter
            @change="(slots) => (selectedSlots = slots)"
            title="Slot"
            :values="slots"
            v-slot="{ value }"
          >
            <Icon
              :show-set="false"
              :show-toon="false"
              size="small"
              :mod="{ slot: value }"
            />
          </Filter>
          <Filter
            @change="(sets) => (selectedSets = sets)"
            title="Set"
            :values="sets"
            v-slot="{ value }"
          >
            <Icon
              :show-slot="false"
              :show-toon="false"
              size="small"
              :mod="{ set: value }"
            />
          </Filter>
          <Filter
            title="Equipped"
            @change="(equipped) => (selectedEquipped = equipped)"
            :values="equippedFilters"
            :multiple="false"
          />
          <Filter
            title="Primaries"
            @change="(primaries) => (selectedPrimaries = primaries)"
            :values="primaries"
          />
        </section>
      </Tab>
      <Tab label="Filter secondaries">
        <section class="secondary-filter">
          <h3>Secondaries</h3>
          <div v-for="(filter, index) in secondaryFilters" :key="index">
            <select v-model="filter.stat">
              <option
                v-for="(value, statName) in secondaryStat"
                :value="value"
                :key="value"
              >
                {{ statName }}
              </option>
            </select>
            <select v-model="filter.op">
              <option value=">=">&gt;=</option>
              <option value="=">=</option>
              <option value="<=">&lt;=</option>
            </select>
            <input name="stat-value" type="text" v-model="filter.value" />
            <button @click="secondaryFilters.splice(index, 1)">Remove</button>
          </div>
          <button
            v-if="secondaryFilters.length < 4"
            @click="secondaryFilters.push({ stat: 5, op: '>=', value: 0 })"
          >
            Add secondary filter
          </button>
        </section>
      </Tab>
      <Tab label="Sort">
        <section class="toolbox">
          <Filter
            @change="(sortBy) => (sortValue = sortBy)"
            :multiple="false"
            :values="sortFunctions"
          />
          <Filter
            @change="(direction) => (sortDirection = direction)"
            :multiple="false"
            :values="[
              { label: 'Ascending', value: 'asc' },
              { label: 'Descending', value: 'desc' },
            ]"
          />
        </section>
      </Tab>
      <Tab label="Remod character">
        <ToonStats
          :units="roster"
          ref="toonStats"
          @selected-slot="toggleSlot"
          @assign="enrichFn = $event"
          @optimize="optimizeBy = $event"
        />
      </Tab>
      <Tab label="Stats">
        <div>Stats</div>
      </Tab>
      <Tab label="Guide">
        <guide />
      </Tab>
    </Tabs>

    <h2 v-if="players.length > 1">Players</h2>
    <section class="players" v-if="players.length > 1">
      <label
        v-for="(player, index) in players"
        :key="player.allyCode"
        :class="{ selected: selectedPlayer == index }"
      >
        <input
          type="radio"
          name="selectedPlayer"
          v-model="selectedPlayer"
          :value="index"
        />
        {{ player.playerName }}
      </label>
    </section>

    <mods-list
      v-if="!showStats"
      :mods="mods"
      @intersected="intersected"
      :player-name="playerName"
      :ally-code="allyCode"
      @selected="select($event)"
    />
    <mods-stats v-if="showStats" :mods="modsFromPlayer" />
    <Modal v-if="selectedMod">
      <template v-if="selectedMod.pips == 6">
        <h2>Mod rolls quality</h2>
        <chart :mod="selectedMod" />
      </template>
      <template v-else>
        <h2>6E simulations</h2>
        <Mod :mod="selectedMod6Dot" />
      </template>
    </Modal>
  </section>
</template>

<script>
import primary_stat_names from "./primary_stat_names.json";
import Tabs from "./Tabs.vue";
import Tab from "./Tab.vue";
import Guide from "./Guide.vue";
import Mod from "./Mod.vue";
import Filter from "./Filter.vue";
import Icon from "./Icon.vue";
import Modal from "./Modal.vue";
import Chart from "./Chart.vue";
import ToonStats from "./ToonStats.vue";
import ModsList from "./ModsList.vue";
import ModsStats from "./ModsStats.vue";
import { calcPercentage, simulate6dots, transformPlayer } from "/@/utils";

export default {
  name: "PlayerFetcher",
  components: {
    Mod,
    Filter,
    Icon,
    Modal,
    Chart,
    Guide,
    Tabs,
    Tab,
    ToonStats,
    ModsList,
    ModsStats,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (to.params.allyCodeToFetch) {
        vm.fetchPlayer(to.params.allyCodeToFetch);
      }
    });
  },
  async beforeRouteUpdate(to, from, next) {
    await this.fetchPlayer(to.params.allyCodeToFetch);
    next();
  },
  data() {
    return {
      allyCodeToFetch: "",
      errorMessage: "",
      isFetching: false,
      apiPlayer: {},
      loadedCount: 20,
      uploadedFile: "",
      optimizerJson: "",
      selectedMod: null,
      primaries: [
        { label: "Health", value: 55 },
        { label: "Protection", value: 56 },
        { label: "Defense", value: 49 },
        { label: "Offense", value: 48 },
        { label: "Potency", value: 17 },
        { label: "Tenacity", value: 18 },
        { label: "Speed", value: 5 },
        { label: "Critical Damage", value: 16 },
        { label: "Critical Chance", value: 53 },
        { label: "Accuracy", value: 46 },
        { label: "Critical Avoidance", value: 54 },
      ],
      selectedPrimaries: [],
      tiers: [
        { label: "E", value: 1 },
        { label: "D", value: 2 },
        { label: "C", value: 3 },
        { label: "B", value: 4 },
        { label: "A", value: 5 },
      ],
      selectedTiers: [],
      rarity: [
        { label: "5 dots", value: 5 },
        { label: "6 dots", value: 6 },
      ],
      selectedRarity: [],
      slots: [
        { label: "Square", value: 0 },
        { label: "Arrow", value: 1 },
        { label: "Diamond", value: 2 },
        { label: "Triangle", value: 3 },
        { label: "Circle", value: 4 },
        { label: "Cross", value: 5 },
      ],
      selectedSlots: [],
      sets: Object.entries(primary_stat_names).map(([value, label]) => ({
        label,
        value: parseInt(value),
      })),
      selectedSets: [],
      equippedFilters: [
        { label: "All mods", value: "all" },
        { label: "Equipped", value: "equipped" },
        { label: "Unequipped", value: "unequipped" },
      ],
      selectedEquipped: "all",
      sortFunctions: [
        { label: "Speed", value: 5 },
        { label: "Critical Chance", value: 53 },
        { label: "Defense", value: 42 },
        { label: "Defense %", value: 49 },
        { label: "Health", value: 1 },
        { label: "Health %", value: 55 },
        { label: "Offense", value: 41 },
        { label: "Offense %", value: 48 },
        { label: "Potency", value: 17 },
        { label: "Protection", value: 28 },
        { label: "Protection %", value: 56 },
        { label: "Tenacity", value: 18 },
        { label: "Best secondary", value: "BestSecondary" },
        { label: "Best average", value: "BestAverage" },
        { label: "Most single-stat rolls", value: "MostRolls" },
      ],
      sortValue: undefined,
      sortDirection: "desc",
      selectedPlayer: 0,
      secondaryFilters: [],
      secondaryStat: {
        Health: 1,
        "Health %": 55,
        Offense: 41,
        "Offense %": 48,
        Defense: 42,
        "Defense %": 49,
        Speed: 5,
        "Critical Chance %": 53,
        "Potency %": 17,
        "Tenacity %": 18,
        Protection: 28,
        "Protection %": 56,
      },
      isRemodding: false,
      enrichFn: () => ({}),
      optimizeBy: undefined,
      showStats: false,
    };
  },
  computed: {
    players() {
      if (!this.optimizerJson) {
        return [];
      }
      return this.optimizerJson.profiles.map(({ allyCode, playerName }) => ({
        allyCode,
        playerName,
      }));
    },
    mods() {
      let enrich = () => ({});
      let sortFunction = this.sortBy(this.sortValue);

      if (this.isRemodding) {
        enrich = this.enrichFn;
        sortFunction = this.sortByChange(this.optimizeBy);
      }
      let mods = this.modsFromPlayer
        .map((mod) => ({ ...mod, change: enrich(mod) }))
        .filter(this.filterMods(this.selectedTiers, "tier"))
        .filter(this.filterMods(this.selectedRarity, "pips"))
        .filter(this.filterMods(this.selectedSlots, "slot"))
        .filter(this.filterMods(this.selectedSets, "set"))
        .filter(this.filterEquipped.bind(this))
        .filter(this.filterPrimary.bind(this))
        .filter(this.filterSecondaries.bind(this))
        .sort(sortFunction);
      if (this.sortDirection == "asc") {
        mods.reverse();
      }

      return mods.slice(0, this.loadedCount);
    },
    playerName() {
      if (this.players.length > 0) {
        return this.players[this.selectedPlayer].playerName;
      }
      if (this.apiPlayer && this.apiPlayer.name) {
        return this.apiPlayer.name;
      }
      return "";
    },
    selectedMod6Dot() {
      return simulate6dots(this.selectedMod);
    },
    userMessage() {
      if (this.isFetching) {
        return `Fetching ally code ${this.allyCode}`;
      }
      return `Showing mods for ${this.playerName} (${this.allyCode})`;
    },
    optimizerMods() {
      return this.optimizerJson
        ? this.optimizerJson.profiles[this.selectedPlayer]
        : undefined;
    },
    modsFromPlayer() {
      if (this.optimizerMods) {
        const mods = transformPlayer(
          this.transformOptimizerMods(this.optimizerMods)
        );
        return mods;
      }
      return transformPlayer(this.apiPlayer);
    },
    roster() {
      return this.apiPlayer.roster || [];
    },
    allyCode() {
      if (this.players.length > 0) {
        return this.players[this.selectedPlayer].allyCode;
      }
      return this.allyCodeToFetch;
    },
  },
  methods: {
    explorePlayer(event) {
      this.$router.push({
        name: "ExploreByAllyCode",
        params: { allyCodeToFetch: event.target[0].value },
      });
    },
    async fetchPlayer(allyCode) {
      this.errorMessage = "";
      if (!allyCode || allyCode.length != 9) {
        this.errorMessage = "Invalid ally code. Must be 9 numbers long";
      }

      this.allyCodeToFetch = allyCode;
      this.isFetching = true;
      try {
        const response = await fetch(
          `https://api.swgoh.help/swgoh/player/${allyCode}`
        );
        this.apiPlayer = (await response.json())[0];
      } catch (e) {
        this.errorMessage = e.message;
      } finally {
        this.isFetching = false;
      }
    },
    intersected(index) {
      this.loadedCount = Math.max(this.loadedCount, index + 10);
    },
    filterMods(selection, property) {
      return function (mod) {
        if (selection.length == 0) {
          return true;
        }
        return selection.includes(mod[property]);
      };
    },
    filterEquipped(mod) {
      if (this.selectedEquipped == "equipped") {
        return mod.toon != null;
      }
      if (this.selectedEquipped == "unequipped") {
        return mod.toon == null;
      }
      return true;
    },
    filterPrimary(mod) {
      if (this.selectedPrimaries.length == 0) {
        return true;
      }
      return this.selectedPrimaries.includes(mod.primaryStat.unitStat);
    },
    filterSecondaries(mod) {
      if (this.secondaryFilters.length == 0) {
        return true;
      }
      return this.secondaryFilters.every((filter) => {
        const secondary = mod.secondaryStat.find(
          ({ unitStat }) => unitStat == filter.stat
        );
        if (!secondary) return false;
        if (filter.op == ">=") {
          return secondary.value >= +filter.value;
        }
        if (filter.op == "<=") {
          return secondary.value <= +filter.value;
        }
        return secondary.value == +filter.value;
      });
    },
    sortBy(sortValue) {
      function getValue(secondaries, sortValue) {
        const secondary = secondaries.find(
          ({ unitStat }) => unitStat == sortValue
        ) || { value: 0 };
        return secondary.value;
      }

      if (!sortValue) {
        return () => {};
      }
      if (`sortBy${sortValue}` in this) {
        return this[`sortBy${sortValue}`];
      }
      return (modA, modB) => {
        let valueA = getValue(modA.secondaryStat, sortValue);
        let valueB = getValue(modB.secondaryStat, sortValue);
        return valueB - valueA;
      };
    },
    sortByBestSecondary(modA, modB) {
      function getBestSecondary(secondaries, pips) {
        return (
          secondaries
            .filter(({ roll }) => roll > 1)
            .map((stat) => calcPercentage(stat, pips))
            .sort((a, b) => b - a)
            .slice(0, 1)[0] || 0
        );
      }

      let bestA = getBestSecondary(modA.secondaryStat, modA.pips);
      let bestB = getBestSecondary(modB.secondaryStat, modB.pips);
      return bestB - bestA;
    },
    sortByBestAverage(modA, modB) {
      function getAverage(secondaries, pips) {
        return secondaries
          .map((stat) => calcPercentage(stat, pips))
          .reduce((a, b) => a + b);
      }
      let averageA = getAverage(modA.secondaryStat, modA.pips);
      let averageB = getAverage(modB.secondaryStat, modB.pips);
      return averageB - averageA;
    },
    sortByMostRolls(modA, modB) {
      function getMaxRolls(secondaries) {
        return (
          secondaries
            .map(({ roll }) => roll)
            .sort((a, b) => b - a)
            .slice(0, 1)[0] || 0
        );
      }

      let rollsA = getMaxRolls(modA.secondaryStat);
      let rollsB = getMaxRolls(modB.secondaryStat);
      return rollsB - rollsA;
    },
    sortByChange(statName) {
      const mapStatNames = {
        "Physical Damage": { stat: "Offense", index: 0, multiValue: true },
        "Special Damage": { stat: "Offense", index: 1, multiValue: true },
        "Physical Critical Chance": {
          stat: "Critical Chance",
          index: -1,
          multiValue: false,
        },
        "Special Critical Chance": {
          stat: "Critical Chance",
          index: -1,
          multiValue: false,
        },
        Armor: { stat: "Defense", index: 0, multiValue: true },
        Resistance: { stat: "Defense", index: 1, multiValue: true },
      };
      const { stat, index, multiValue } = mapStatNames[statName] || {
        stat: statName,
        index: -1,
        multiValue: false,
      };
      return (modA, modB, ...args) => {
        if (!stat) {
          return 0;
        }
        const fallback = multiValue ? "-100000/-100000" : "-100000";
        const changeA = modA.change[stat] || fallback;
        const changeB = modB.change[stat] || fallback;
        if (!multiValue) {
          return +changeB - +changeA;
        } else {
          return +changeB.split("/")[index] - +changeA.split("/")[index];
        }
      };
    },
    processFile(event) {
      this.uploadedFile = event.target.files[0];
    },
    async parseModsOptimizerJson() {
      this.errorMessage = "";
      try {
        this.optimizerJson = JSON.parse(await this.uploadedFile.text());
      } catch (e) {
        this.errorMessage = e.message;
        return;
      }
      if (
        !this.optimizerJson.profiles ||
        this.optimizerJson.profiles.length == 0
      ) {
        this.errorMessage =
          "No profiles found. Is is a valid export from the mods optimizer";
        return;
      }
      await this.fetchPlayer(this.allyCode);
    },
    transformOptimizerMods(player) {
      let slots = ["square", "arrow", "diamond", "triangle", "circle", "cross"];
      let sets = [
        "health",
        "offense",
        "defense",
        "speed",
        "critchance",
        "critdamage",
        "potency",
        "tenacity",
      ];
      let stats = {
        ...this.secondaryStat,
        "Critical Damage %": 16,
        "Accuracy %": 46,
        "Critical Avoidance %": 54,
      };

      return {
        roster: player.mods.map((mod) => ({
          defId: mod.characterID || null,
          mods: [
            {
              id: mod.mod_uid,
              pips: mod.pips,
              level: mod.level,
              set: sets.indexOf(mod.set) + 1,
              slot: slots.indexOf(mod.slot) + 1,
              tier: mod.tier,
              primaryStat: {
                unitStat: stats[mod.primaryBonusType],
                value: parseFloat(mod.primaryBonusValue),
                roll: 0,
              },
              secondaryStat: [
                {
                  unitStat: stats[mod.secondaryType_1],
                  value: parseFloat(mod.secondaryValue_1),
                  roll: mod.secondaryRoll_1,
                },
                {
                  unitStat: stats[mod.secondaryType_2],
                  value: parseFloat(mod.secondaryValue_2),
                  roll: mod.secondaryRoll_2,
                },
                {
                  unitStat: stats[mod.secondaryType_3],
                  value: parseFloat(mod.secondaryValue_3),
                  roll: mod.secondaryRoll_3,
                },
                {
                  unitStat: stats[mod.secondaryType_4],
                  value: parseFloat(mod.secondaryValue_4),
                  roll: mod.secondaryRoll_4,
                },
              ].filter(({ unitStat }) => unitStat),
            },
          ],
        })),
      };
    },
    select(mod) {
      if (this.isRemodding) {
        this.$refs.toonStats.assignMod(mod);
      } else {
        this.selectedMod = mod;
      }
    },
    updateClickEvent(index, label) {
      if (this.isRemodding && label != "Remod character") {
        this.isRemodding = false;
      } else if (!this.isRemodding && label == "Remod character") {
        this.isRemodding = true;
      }
      if (this.showStats && label != "Stats") {
        this.showStats = false;
      } else if (!this.showStats && label == "Stats") {
        this.showStats = true;
      }
    },
    toggleSlot(slot) {
      if (this.selectedSlots.length == 1 && this.selectedSlots[0] == slot) {
        this.selectedSlots = [];
      } else {
        this.selectedSlots = [slot];
      }
    },
  },
  watch: {
    async selectedPlayer() {
      await this.fetchPlayer(this.allyCode);
    },
  },
};
</script>

<style lang="scss">
.error-message {
  color: red;
}

.mod-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.mod-list > * {
  flex: 0 1 auto;
}

.toolbox {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
}

.show-guide {
  padding: 1rem 10rem;
}

.players {
  label {
    background-color: dimgray;
    border: 0.25rem solid lavender;
    color: hsl(224.2, 33.3%, 88.8%);
    padding: 1rem;
    display: inline-block;
  }
}

select {
  padding: 1.5rem;
  font-size: 1.25rem;
}
</style>
